import { alpha, Stack, Typography } from '@mui/material';
import { Chip, colorPaletteLight, Modal, Switch } from '@sb/cc-design-system';
import { toString } from 'lodash';
import { memo, useEffect, useState } from 'react';
import { FilterDropdownWithSearch } from 'src/components/filterDropdownWithSearch';
import { Progress } from 'src/components/progress/Progress';
import { filters } from 'src/features/locations/locationListPage/hooks/useLocationFilters';
import { lang } from 'src/utils';

import { paramsKey } from '../filterList/FilterList.const';
import { SearchBar } from '../input/SearchBar';
import { FilterModalProps } from './FilterModal.interface';
import {
  CloseIconPointer,
  DropdownContainer,
  ExactMatchContainer,
  FilterLabel,
  FilterSectionContainer,
  SearchInputContainer,
} from './FilterModal.styled';

export const FilterModal = memo((props: FilterModalProps) => {
  const {
    open,
    onClose,
    onApply,
    onChangeFilter,
    isFuzzySearch,
    setIsFuzzySearch,
    activeFilters,
  } = props;

  // Local state for filters until Apply is clicked
  const [localFilters, setLocalFilters] = useState<
    Record<string, string | string[]>
  >({});
  const [localSearchVal, setLocalSearchVal] = useState<string>('');
  const [localSearchValues, setLocalSearchValues] = useState<string[]>([]);
  // Local state for freeSearch filter terms (e.g. store_number)
  const [freeSearchTerms, setFreeSearchTerms] = useState<
    Record<string, string[]>
  >({});
  const [freeSearchInput, setFreeSearchInput] = useState<
    Record<string, string>
  >({});
  // Independent fuzzy/exact state for each freeSearch filter
  const [freeSearchFuzzy, setFreeSearchFuzzy] = useState<
    Record<string, boolean>
  >(() => {
    const fuzzyState: Record<string, boolean> = {};
    filters?.forEach((filter) => {
      if (filter.fieldType === 'freeSearch') {
        const baseKey = paramsKey(filter.label);
        fuzzyState[baseKey] = true;
      }
    });

    return fuzzyState;
  });

  const [localIsFuzzySearch, setLocalIsFuzzySearch] =
    useState<boolean>(isFuzzySearch);

  const [initialSnapshot, setInitialSnapshot] = useState<{
    localFilters: Record<string, string | string[]>;
    localSearchValues: string[];
    freeSearchTerms: Record<string, string[]>;
    freeSearchFuzzy: Record<string, boolean>;
    isFuzzySearch: boolean;
  } | null>(null);

  // Initialize local state from parent props when modal opens
  useEffect(() => {
    if (open) {
      let nextLocalFilters: Record<string, string | string[]> = {};
      if (activeFilters) {
        nextLocalFilters = { ...activeFilters };
        setLocalFilters(nextLocalFilters);
      }
      // Get search values from name.regexp or name.list from activeFilters
      const nameRegexpValues =
        activeFilters?.['name.regexp'] || activeFilters?.['name.list'];
      const searchValues = Array.isArray(nameRegexpValues)
        ? nameRegexpValues
        : nameRegexpValues
        ? [nameRegexpValues]
        : [];
      setLocalSearchValues(searchValues);
      setLocalIsFuzzySearch(isFuzzySearch);
      setLocalSearchVal('');

      // Initialize freeSearch filter terms from activeFilters
      const terms: Record<string, string[]> = {};
      const fuzzyState: Record<string, boolean> = {};
      filters?.forEach((filter) => {
        if (filter.fieldType === 'freeSearch') {
          const baseKey = paramsKey(filter.label); // e.g. 'store_number.regexp' or 'store_number.list'
          const listKey = baseKey.replace(/\.regexp$/, '.list');
          const regexpKey = baseKey.replace(/\.list$/, '.regexp');
          const hasList = activeFilters?.[listKey];
          const hasRegexp = activeFilters?.[regexpKey];
          const val = hasList || hasRegexp;
          const parsedVal = Array.isArray(val) ? val : val ? [String(val)] : [];
          terms[baseKey] = parsedVal;
          fuzzyState[baseKey] = !hasList?.length;
        }
      });
      setFreeSearchTerms(terms);
      setFreeSearchFuzzy(fuzzyState);
      setFreeSearchInput({});

      setInitialSnapshot({
        localFilters: nextLocalFilters,
        localSearchValues: searchValues,
        freeSearchTerms: terms,
        freeSearchFuzzy: fuzzyState,
        isFuzzySearch,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeFilters]);

  const isDirty = (() => {
    if (!initialSnapshot) return false;
    const normalize = (obj: Record<string, unknown>) => {
      const result: Record<string, unknown> = {};
      for (const key of Object.keys(obj).sort()) {
        const value = obj[key];
        if (Array.isArray(value) && value.length === 0) continue;
        result[key] = value;
      }

      return result;
    };
    // Only compare freeSearchFuzzy for keys that have actual freeSearchTerms
    const fuzzyWithTerms = (
      fuzzy: Record<string, boolean>,
      terms: Record<string, string[]>,
    ) => {
      const result: Record<string, boolean> = {};
      for (const key of Object.keys(fuzzy)) {
        if ((terms[key] ?? []).length > 0) {
          result[key] = fuzzy[key];
        }
      }

      return result;
    };

    const snapshot = JSON.stringify({
      localFilters: normalize(initialSnapshot.localFilters),
      localSearchValues: initialSnapshot.localSearchValues,
      freeSearchTerms: normalize(initialSnapshot.freeSearchTerms),
      freeSearchFuzzy: fuzzyWithTerms(
        initialSnapshot.freeSearchFuzzy,
        initialSnapshot.freeSearchTerms,
      ),
      ...(initialSnapshot.localSearchValues?.length > 0 && {
        isFuzzySearch: initialSnapshot.isFuzzySearch,
      }),
    });

    const current = JSON.stringify({
      localFilters: normalize(localFilters),
      localSearchValues,
      freeSearchTerms: normalize(freeSearchTerms),
      freeSearchFuzzy: fuzzyWithTerms(freeSearchFuzzy, freeSearchTerms),
      ...(localSearchValues?.length > 0 && {
        isFuzzySearch: localIsFuzzySearch,
      }),
    });

    return snapshot !== current;
  })();

  // Get filter value from local state
  const getLocalFilterValue = (key: string): string[] => {
    const value = localFilters[key];
    if (Array.isArray(value)) {
      return value;
    }
    if (value) {
      return [String(value)];
    }

    return [];
  };

  const handleLocalFilterChange = (key: string, value: string | string[]) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleReset = () => {
    // Clear local filters
    let resetFilters = {} as Record<string, string | string[]>;
    Object.keys(localFilters).forEach((key) => {
      resetFilters[key] = [];
    });
    setLocalFilters(resetFilters);
    setLocalSearchVal('');
    setLocalSearchValues([]);
    setFreeSearchTerms({});
    setFreeSearchInput({});
    setLocalIsFuzzySearch(true);
    const fuzzyState: Record<string, boolean> = {};
    filters?.forEach((filter) => {
      if (filter.fieldType === 'freeSearch') {
        const baseKey = paramsKey(filter.label);
        fuzzyState[baseKey] = true;
      }
    });
    setFreeSearchFuzzy(fuzzyState);
  };

  const handleApply = () => {
    // Apply all local filter changes to parent, including cleared filters
    Object.entries(localFilters).forEach(([key, value]) => {
      onChangeFilter(key, Array.isArray(value) ? value : toString(value));
    });

    // Main location search: if user didn't enter any term,
    // exact-match toggle must default back to OFF (fuzzy mode).
    const shouldKeepMainExact = localSearchValues.length > 0;
    const appliedIsFuzzySearch = shouldKeepMainExact
      ? localIsFuzzySearch
      : true;

    const mainSearchParamKey = appliedIsFuzzySearch
      ? 'name.regexp'
      : 'name.list';
    const mainSearchOtherKey = appliedIsFuzzySearch
      ? 'name.list'
      : 'name.regexp';

    onChangeFilter(
      mainSearchParamKey,
      shouldKeepMainExact ? localSearchValues : [],
    );
    onChangeFilter(mainSearchOtherKey, []);
    setIsFuzzySearch(appliedIsFuzzySearch);

    // Apply freeSearch filter terms (store_number): same rule as above.
    Object.entries(freeSearchTerms).forEach(([baseKey, values]) => {
      const listKey = baseKey.replace(/\.regexp$/, '.list');
      const regexpKey = baseKey.replace(/\.list$/, '.regexp');

      const hasTerms = values.length > 0;
      const effectiveIsExact = hasTerms ? !freeSearchFuzzy[baseKey] : false;

      const paramKey = effectiveIsExact ? listKey : regexpKey;
      const otherKey = effectiveIsExact ? regexpKey : listKey;

      onChangeFilter(paramKey, values);
      onChangeFilter(otherKey, []);
    });
    onApply();
    onClose();
  };

  const handleAddSearchValue = () => {
    if (localSearchVal.trim() && !localSearchValues.includes(localSearchVal)) {
      setLocalSearchValues([...localSearchValues, localSearchVal]);
      setLocalSearchVal('');
    }
    // here add the local fitlers
  };

  const handleRemoveSearchValue = (valueToRemove: string) => {
    setLocalSearchValues(
      localSearchValues.filter((val) => val !== valueToRemove),
    );
  };

  const handleClose = () => {
    if (initialSnapshot) {
      setLocalIsFuzzySearch(initialSnapshot.isFuzzySearch);
      setFreeSearchFuzzy(initialSnapshot.freeSearchFuzzy);
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={lang.filters ?? 'Filters'}
      disableBackdropClick
      sizeVariant="medium"
      confirmButtonText="Apply"
      cancelButtonText={lang.clearFilter}
      onCancelAction={handleReset}
      onConfirmAction={handleApply}
      cancelButtonProps={{ variant: 'text' }}
      confirmButtonProps={{
        disabled: !isDirty,
      }}
      footerStyle={{ justifyContent: 'space-between' }}
      headerStyle={{
        padding: '0 16px 0 16px',
        alignItems: 'center',
      }}>
      <ExactMatchContainer>
        <Typography
          variant="body1"
          style={{
            color: alpha(colorPaletteLight.neutral.text.primary, 0.38),
          }}>
          {lang.exactMatch}
        </Typography>

        <Switch
          checked={!localIsFuzzySearch}
          onChange={() => setLocalIsFuzzySearch(!localIsFuzzySearch)}
          size="medium"
        />
      </ExactMatchContainer>

      {/* Search Section at Top */}
      <SearchInputContainer>
        <SearchBar
          placeholder={lang.search}
          value={localSearchVal}
          onInputChange={(value) => setLocalSearchVal(String(value))}
          onKeyDown={(e) => e.key === 'Enter' && handleAddSearchValue()}
          height={48}
        />
      </SearchInputContainer>
      {/* Search Values Chips */}
      {localSearchValues.length > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          gap={0.5}
          sx={{ mb: 1 }}>
          {localSearchValues.map((value, index) => (
            <Chip
              key={`local-search-${index}`}
              label={value}
              onEndIconClick={() => handleRemoveSearchValue(value)}
              endIcon={<CloseIconPointer />}
              textColor={colorPaletteLight.secondary.info.deep}
              variant="filled"
              chipBackgroundColor={colorPaletteLight.secondary.info.subtle}
            />
          ))}
        </Stack>
      )}

      {/* Filter Sections */}
      <>
        {filters?.map((filter, key) => {
          const label = paramsKey(filter?.label);
          const values = getLocalFilterValue(label);

          return (
            <FilterSectionContainer key={key}>
              <FilterLabel>
                <Typography variant="h6" fontWeight={500}>
                  {filter.labelOverride ?? filter.label}
                </Typography>
              </FilterLabel>

              {filter.isLoading && <Progress />}
              {filter.isError && <Typography>{lang.error}</Typography>}

              {!filter.isLoading &&
                !filter.isError &&
                (filter.fieldType === 'freeSearch' ? (
                  <>
                    <ExactMatchContainer>
                      <Typography
                        variant="body1"
                        style={{
                          color: alpha(
                            colorPaletteLight.neutral.text.primary,
                            0.38,
                          ),
                        }}>
                        {lang.exactMatch}
                      </Typography>

                      <Switch
                        checked={!freeSearchFuzzy[label]}
                        onChange={() =>
                          setFreeSearchFuzzy((prev) => ({
                            ...prev,
                            [label]: !prev[label],
                          }))
                        }
                        size="medium"
                      />
                    </ExactMatchContainer>
                    <SearchInputContainer>
                      <SearchBar
                        height={48}
                        placeholder={
                          filter.placeholder ??
                          filter.labelOverride ??
                          filter.label
                        }
                        value={freeSearchInput[label] || ''}
                        onInputChange={(val) =>
                          setFreeSearchInput((prev) => ({
                            ...prev,
                            [label]: String(val),
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const val = (
                              e.target as HTMLInputElement
                            ).value.trim();
                            if (val) {
                              const current = freeSearchTerms[label] || [];
                              if (!current.includes(val)) {
                                setFreeSearchTerms((prev) => ({
                                  ...prev,
                                  [label]: [...current, val],
                                }));
                              }
                              setFreeSearchInput((prev) => ({
                                ...prev,
                                [label]: '',
                              }));
                            }
                          }
                        }}
                      />
                    </SearchInputContainer>

                    {(freeSearchTerms[label] || []).length > 0 && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={0.5}
                        sx={{ mb: 1 }}>
                        {(freeSearchTerms[label] || []).map((term, i) => (
                          <Chip
                            key={`${label}-term-${i}`}
                            label={term}
                            textColor={colorPaletteLight.secondary.info.deep}
                            onEndIconClick={() =>
                              setFreeSearchTerms((prev) => ({
                                ...prev,
                                [label]: (prev[label] || []).filter(
                                  (_, idx) => idx !== i,
                                ),
                              }))
                            }
                            endIcon={<CloseIconPointer />}
                            variant="filled"
                            chipBackgroundColor={
                              colorPaletteLight.secondary.info.subtle
                            }
                          />
                        ))}
                      </Stack>
                    )}
                  </>
                ) : (
                  <DropdownContainer>
                    <FilterDropdownWithSearch
                      name={filter.label}
                      multiline={Boolean(filter.badgeOptions?.align)}
                      onChange={(value) => {
                        handleLocalFilterChange(label, value as any);
                      }}
                      value={Array.isArray(values) ? values : []}
                      badgeOptions={{
                        align: 'center',
                        roundedSelect: true,
                        sx: {
                          backgroundColor:
                            colorPaletteLight.secondary.info.subtle,
                          color: colorPaletteLight.secondary.info.deep,
                          fontWeight: 500,
                        },
                        deleteIcon: (
                          <CloseIconPointer
                            style={{
                              color: colorPaletteLight.secondary.info.deep,
                            }}
                          />
                        ),
                      }}
                      placeholder={
                        filter.placeholder ??
                        filter.labelOverride ??
                        filter.label
                      }
                      inputFieldSx={{
                        color: colorPaletteLight.neutral.outline.outline3,
                      }}
                      selectPadding={'14px 16px 12px 16px'}
                      options={{
                        hoverColor: alpha(
                          colorPaletteLight.primary.action.hover,
                          0.1,
                        ),
                      }}
                    />
                  </DropdownContainer>
                ))}
            </FilterSectionContainer>
          );
        })}
      </>
    </Modal>
  );
});

FilterModal.displayName = 'FilterModal';
